/**
 * The show builder: pick, order and configure rounds, then save a `show_formats` row and
 * start a session from it. "Round" here means a segment; Jeopardy's internal one is a "board".
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthGate } from '../components/AuthGate';
import { ConfigForm } from '../show/ConfigForm';
import { api, type GameManifestSummary, type StepDraft } from '../lib/api';
import { rememberGame } from '../lib/myGames';

export function Builder() {
  return (
    <AuthGate title="Build a show">
      <BuilderInner />
    </AuthGate>
  );
}

/** One round on this screen. `key` is local — a round has no id until it is saved. */
interface Round {
  key: number;
  gameId: string;
  packId: string | null;
  title: string;
  config: Record<string, unknown>;
}

interface Pack {
  id: string;
  gameId: string;
  title: string;
}

let nextKey = 1;

/**
 * Can this server play this game at all? Takes packs as an argument, not a closure over
 * state: the initial-round effect runs before `packs` is in state.
 */
function startableGame(game: GameManifestSummary, packs: readonly Pack[]): boolean {
  if (!game.capabilities.needsContent) return true;
  return packs.some((pack) => pack.gameId === game.id);
}

function packsFor(gameId: string, packs: readonly Pack[]): Pack[] {
  return packs.filter((pack) => pack.gameId === gameId);
}

/** Which game a new round starts on. Arguments, not state, for the same reason as `startableGame`. */
function defaultGame(
  games: readonly GameManifestSummary[],
  packs: readonly Pack[],
): GameManifestSummary | undefined {
  return games.find((game) => startableGame(game, packs)) ?? games[0];
}

/** A fresh round on a game, with that game's first pack and no config answers. */
function roundFor(game: GameManifestSummary, packs: readonly Pack[]): Round {
  return {
    key: nextKey++,
    gameId: game.id,
    packId: packsFor(game.id, packs)[0]?.id ?? null,
    title: '',
    config: {},
  };
}

function BuilderInner() {
  const navigate = useNavigate();
  const [games, setGames] = useState<GameManifestSummary[]>([]);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [name, setName] = useState('Game Night');
  const [rounds, setRounds] = useState<Round[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [registry, allPacks] = await Promise.all([api.listGames(), api.listPacks()]);
        setGames(registry);
        setPacks(allPacks);
        const first = defaultGame(registry, allPacks);
        if (first) setRounds([roundFor(first, allPacks)]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the games.');
      } finally {
        setLoaded(true);
      }
    })();
    // Mount only: re-running would throw away everything the host had typed.
  }, []);

  function gameFor(gameId: string): GameManifestSummary | undefined {
    return games.find((game) => game.id === gameId);
  }

  function patch(key: number, change: Partial<Round>): void {
    setRounds((current) =>
      current.map((round) => (round.key === key ? { ...round, ...change } : round)),
    );
  }

  function move(key: number, by: number): void {
    setRounds((current) => {
      const at = current.findIndex((round) => round.key === key);
      const to = at + by;
      if (at < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(at, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  // `estimatedMinutes` was computed with the empty config, so edited rounds read slightly
  // off until saved. Good enough for planning; cheaper than a round trip per keystroke.
  const minutes = rounds.reduce(
    (total, round) => total + (gameFor(round.gameId)?.estimatedMinutes ?? 0),
    0,
  );

  // The server's strictest-segment rule, previewed so the host knows before saving.
  const unhosted =
    rounds.length > 0 &&
    rounds.every((round) => gameFor(round.gameId)?.capabilities.host !== 'required');

  const problems = rounds
    .map((round, at) => {
      const game = gameFor(round.gameId);
      if (!game) return `Round ${at + 1} runs a game this server does not have.`;
      if (!startableGame(game, packs)) {
        return `Round ${at + 1}: ${game.name} has no content on this server yet.`;
      }
      return null;
    })
    .filter((problem): problem is string => problem !== null);

  async function build(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const steps: StepDraft[] = rounds.map((round) => ({
        gameId: round.gameId,
        packId: round.packId,
        title: round.title.trim() || null,
        config: round.config,
      }));
      // Save first: an unhosted show can only be started from a saved format.
      const format = await api.saveFormat({ title: name.trim() || 'My show', steps });
      const session = await api.createSession(name.trim() || format.title, undefined, format.id);
      rememberGame({ code: session.code, name: session.title, role: 'created' });
      navigate(`/host/${session.code}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build that show.');
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <form className="col builder" style={{ width: 'min(46rem, 100%)' }} onSubmit={build}>
        {/* Overrides `.card`'s 28rem so the header matches the rounds' width. */}
        <div className="card col" style={{ width: '100%' }}>
          <h1>Build a show</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Add rounds, pick a game for each, and press start. It is saved so you can run
            the same night again.
          </p>

          <div className="field">
            <label htmlFor="show-name">Show name</label>
            <input
              id="show-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Game Night"
              autoComplete="off"
              maxLength={80}
            />
          </div>

          <p className="faint" style={{ margin: 0 }}>
            {rounds.length} round{rounds.length === 1 ? '' : 's'} · ≈{minutes} min
            {unhosted ? ' · no host needed' : ' · you will need to host this'}
          </p>
        </div>

        {rounds.map((round, at) => {
          const game = gameFor(round.gameId);
          const mine = packsFor(round.gameId, packs);
          return (
            <div className="panel col" key={round.key}>
              <div className="row" style={{ alignItems: 'baseline', gap: '0.5rem' }}>
                <h2 className="panel-title" style={{ margin: 0 }}>
                  Round {at + 1}
                </h2>
                <span className="spacer" />
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={at === 0}
                  onClick={() => move(round.key, -1)}
                  title="Move earlier"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={at === rounds.length - 1}
                  onClick={() => move(round.key, 1)}
                  title="Move later"
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  // A show needs at least one round; the way out is Back.
                  disabled={rounds.length === 1}
                  onClick={() => setRounds((c) => c.filter((r) => r.key !== round.key))}
                  title="Remove this round"
                >
                  ×
                </button>
              </div>

              <div className="field">
                <label htmlFor={`round-game-${at + 1}`}>Game</label>
                <select
                  id={`round-game-${at + 1}`}
                  value={round.gameId}
                  onChange={(e) => {
                    const picked = gameFor(e.target.value);
                    // Pack and config belong to the old game, so reset both.
                    patch(round.key, {
                      gameId: e.target.value,
                      packId: picked ? (packsFor(picked.id, packs)[0]?.id ?? null) : null,
                      config: {},
                    });
                  }}
                >
                  {games.map((option) => (
                    <option
                      key={option.id}
                      value={option.id}
                      disabled={!startableGame(option, packs)}
                    >
                      {option.name}
                      {startableGame(option, packs) ? '' : ' — no content on this server'}
                    </option>
                  ))}
                </select>
                {game ? (
                  <span className="faint">
                    {game.players.min}–{game.players.max} players · ≈{game.estimatedMinutes} min
                    {game.capabilities.host === 'none' ? ' · no host needed' : ''}
                  </span>
                ) : null}
              </div>

              {mine.length > 1 ? (
                <div className="field">
                  <label htmlFor={`round-pack-${at + 1}`}>Content</label>
                  <select
                    id={`round-pack-${at + 1}`}
                    value={round.packId ?? ''}
                    onChange={(e) => patch(round.key, { packId: e.target.value || null })}
                  >
                    {mine.map((pack) => (
                      <option key={pack.id} value={pack.id}>
                        {pack.title}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="field">
                <label htmlFor={`round-title-${at + 1}`}>Call it (optional)</label>
                <input
                  id={`round-title-${at + 1}`}
                  value={round.title}
                  onChange={(e) => patch(round.key, { title: e.target.value })}
                  placeholder={game?.name ?? ''}
                  autoComplete="off"
                  maxLength={60}
                />
              </div>

              {game ? (
                <ConfigForm
                  idPrefix={String(at + 1)}
                  form={game.configForm}
                  value={round.config}
                  onChange={(config) => patch(round.key, { config })}
                />
              ) : null}
            </div>
          );
        })}

        <button
          type="button"
          className="btn"
          disabled={games.length === 0}
          onClick={() => {
            const game = defaultGame(games, packs);
            if (game) setRounds((current) => [...current, roundFor(game, packs)]);
          }}
        >
          + Add a round
        </button>

        {problems.map((problem) => (
          <p className="error-text" key={problem}>
            {problem}
          </p>
        ))}
        {error ? <p className="error-text">{error}</p> : null}

        <button
          className="btn btn-primary btn-lg"
          type="submit"
          disabled={busy || !loaded || rounds.length === 0 || problems.length > 0}
        >
          {busy ? 'Starting…' : `Save and start · ≈${minutes} min`}
        </button>

        <Link className="btn btn-ghost" to="/create">
          ← Back
        </Link>
      </form>
    </div>
  );
}
